/**
 * SeKa VAT Switcher
 * Handles switching between gross (inkl. MwSt.) and net (exkl. MwSt.) price display
 */

(function() {
  'use strict';

  const VAT_STORAGE_KEY = 'seka_vat_mode';
  const DEFAULT_MODE = window.SeKaTheme?.vatDefaultMode || 'gross';

  /**
   * Get current VAT mode from localStorage or default
   */
  function getVatMode() {
    try {
      return localStorage.getItem(VAT_STORAGE_KEY) || DEFAULT_MODE;
    } catch (e) {
      return DEFAULT_MODE;
    }
  }

  /**
   * Set VAT mode in localStorage
   */
  function setVatMode(mode) {
    try {
      localStorage.setItem(VAT_STORAGE_KEY, mode);
    } catch (e) {
      console.warn('Could not save VAT preference to localStorage');
    }
  }

  /**
   * Apply VAT mode to document
   */
  function applyVatMode(mode) {
    document.documentElement.setAttribute('data-vat-mode', mode);
    
    // Update all VAT switcher buttons
    document.querySelectorAll('.vat-switcher__btn').forEach(function(btn) {
      const btnMode = btn.getAttribute('data-vat-mode');
      btn.classList.toggle('is-active', btnMode === mode);
    });

    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('vatModeChanged', {
      detail: { mode: mode }
    }));
  }

  /**
   * Initialize VAT switcher
   */
  function initVatSwitcher() {
    const currentMode = getVatMode();
    applyVatMode(currentMode);

    // Add click handlers to all VAT switcher buttons
    document.querySelectorAll('.vat-switcher__btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        const newMode = this.getAttribute('data-vat-mode');
        if (newMode && newMode !== getVatMode()) {
          setVatMode(newMode);
          applyVatMode(newMode);
        }
      });
    });
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  function formatMoney(priceInCents) {
    const format = window.SeKaTheme?.moneyFormat || '{{amount_with_comma_separator}} €';
    const amount = (priceInCents / 100).toFixed(2);
    const amountWithComma = amount.replace('.', ',');
    
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(priceInCents / 100))
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(priceInCents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /**
   * Calculate prices based on Shopify tax configuration
   * @param {number} shopifyPrice - Price from Shopify in cents
   * @returns {object} { gross, net } prices in cents
   */
  function calculatePrices(shopifyPrice) {
    const vatRate = window.SeKaTheme?.vatRate || 19;
    const vatMultiplier = 100 + vatRate;
    const taxesIncluded = window.SeKaTheme?.taxesIncluded ?? true;
    
    if (taxesIncluded) {
      return {
        gross: shopifyPrice,
        net: Math.round(shopifyPrice * 100 / vatMultiplier)
      };
    } else {
      return {
        net: shopifyPrice,
        gross: Math.round(shopifyPrice * vatMultiplier / 100)
      };
    }
  }

  /**
   * Calculate net price from gross price (legacy support)
   * @param {number} grossPrice - Gross price in cents
   * @returns {number} Net price in cents
   */
  function calculateNetPrice(grossPrice) {
    const vatRate = window.SeKaTheme?.vatRate || 19;
    return Math.round(grossPrice * 100 / (100 + vatRate));
  }

  /**
   * Update all dynamic prices on the page
   * This is useful for AJAX-loaded content
   */
  function updateDynamicPrices() {
    const mode = getVatMode();
    
    document.querySelectorAll('[data-dynamic-price]').forEach(function(el) {
      const grossPrice = parseInt(el.getAttribute('data-price-gross'), 10);
      if (isNaN(grossPrice)) return;
      
      const netPrice = calculateNetPrice(grossPrice);
      const displayPrice = mode === 'net' ? netPrice : grossPrice;
      
      el.textContent = formatMoney(displayPrice);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVatSwitcher);
  } else {
    initVatSwitcher();
  }

  // Expose functions globally for use in other scripts
  window.SeKaVat = {
    getMode: getVatMode,
    setMode: function(mode) {
      setVatMode(mode);
      applyVatMode(mode);
    },
    calculatePrices: calculatePrices,
    calculateNetPrice: calculateNetPrice,
    formatMoney: formatMoney,
    updateDynamicPrices: updateDynamicPrices
  };
})();
